import { AbortSignalLike, ApiResult, HttpClient, withSignal } from "../http.js";
import {
  PagedProjects,
  ProjectDetails,
  ProjectDto,
  ProjectLookupDto,
  ProjectPartnerDto,
  RemoveProjectPartnerResult,
  ProjectPartnerRole,
  ProjectStatus,
  ProjectType,
  TimeplanCustomerNotificationDto,
} from "../types/projects.js";

/** Body of `POST /api/projects/{id}/partners`. `role` goes out as its name. */
export interface AddProjectPartnerPayload {
  partnerCustomerId: string;
  role: ProjectPartnerRole;
  subProjectId?: string | null;
}

/** Body of `PUT /api/projects/{id}/partners/{partnerId}`; a null `subProjectId` clears it. */
export interface UpdateProjectPartnerPayload {
  role: ProjectPartnerRole;
  subProjectId?: string | null;
}

export interface CreateProjectPayload {
  title: string;
  type: ProjectType;
  customerId: string;
  startDate?: string | null;
  endDate?: string | null;
  projectManagerId?: string | null;
  description?: string | null;
  budgetExclVat?: number | null;
  budgetInclVat?: number | null;
  currency?: string | null;
  number?: string | null;
  parentProjectId?: string | null;
  id?: string;
  details?: ProjectDetails | null;
  /** Ejendom link, optional at create. The free-text address keeps working. */
  propertyId?: string | null;
  propertyUnitId?: string | null;
  /** Who is invoiced when it is not the customer. A sub-project without one inherits the parent's. */
  billingCustomerIdOverride?: string | null;
  /** Anlæg to link at create, which makes the project a serviceordre. */
  assetIds?: string[] | null;
}

export interface UpdateProjectPayload {
  title: string;
  type: ProjectType;
  customerId: string;
  startDate?: string | null;
  endDate?: string | null;
  projectManagerId?: string | null;
  description?: string | null;
  budgetExclVat?: number | null;
  budgetInclVat?: number | null;
  currency?: string | null;
  details?: ProjectDetails | null;

  // ---- Ejendom link ----
  // Omit to leave the stored link alone; send an explicit null to clear it. Clearing the
  // property clears the unit with it. The free-text address is untouched either way.
  propertyId?: string | null;
  propertyUnitId?: string | null;
  billingCustomerIdOverride?: string | null;
  /**
   * The `version` of the project the caller read. When set and the project has changed since,
   * the API answers 409 `project_version_conflict`. Omit for last-write-wins (#4617).
   */
  version?: number;
}

export interface ListProjectsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ProjectStatus;
  customerId?: string;
  projectManagerId?: string;
  parentProjectId?: string;
  /**
   * Descendants of the given root, **excluding the root row itself** — a root stores
   * `rootProjectId: null`, so it never matches. Server-side, so callers after a whole
   * tree no longer have to drain every page and filter in memory.
   */
  rootProjectId?: string;
  fromDate?: string;
  toDate?: string;
  /**
   * Every project booked against one ejendom. Server-side, because a building's job
   * history is exactly what a free-text address could never be asked for.
   */
  propertyId?: string;
  /**
   * Root-level projects only (`ParentProjectId IS NULL`). For pickers that used to drain
   * every page and filter `!parentProjectId` client-side. Ignored when `parentProjectId`
   * is also set.
   */
  rootOnly?: boolean;
  /**
   * Matches any of the given statuses. Ignored when `status` is also set. Sent as a
   * repeated query key (`?statuses=Active&statuses=Planned`) — see {@link HttpClient}'s
   * query-array handling.
   */
  statuses?: readonly ProjectStatus[];
  /**
   * Projects this user leads (`projectLeaderIds`) or has logged time on — the employee
   * page's project list, without draining the whole company to find them.
   */
  involvedUserId?: string;
}

export interface NotifyTimeplanCustomerPayload {
  phaseTitle: string;
  changeDescription: string;
}

export class ProjectsApi {
  constructor(private readonly http: HttpClient) {}

  /**
   * `signal` is forwarded to fetch so a caller that pages this endpoint can be
   * aborted mid-drain. Without it an abandoned read keeps running after the
   * component unmounts — 37-39 requests and ~16 MB that nobody is waiting for
   * any more (#2196).
   */
  list(query: ListProjectsQuery = {}, options: { signal?: AbortSignalLike } = {}): Promise<ApiResult<PagedProjects>> {
    // Spread rather than `signal: options.signal`: under exactOptionalPropertyTypes
    // an explicit `undefined` is not the same as an absent property.
    return this.http.get<PagedProjects>("/api/projects", {
      query: { ...query },
      ...withSignal(options),
    });
  }

  /**
   * Every non-archived project in the org as a lean row — unpaged, one request.
   * Use this instead of paging {@link list} when you need to render or group
   * projects (pickers) rather than read their full state.
   */
  lookup(options: { signal?: AbortSignalLike } = {}): Promise<ApiResult<ProjectLookupDto[]>> {
    return this.http.get<ProjectLookupDto[]>("/api/projects/lookup", withSignal(options));
  }

  get(id: string): Promise<ApiResult<ProjectDto>> {
    return this.http.get<ProjectDto>(`/api/projects/${encodeURIComponent(id)}`);
  }

  create(payload: CreateProjectPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/projects", { body: payload });
  }

  /** Links an anlæg to the project. Linking twice is a no-op. */
  linkAsset(id: string, assetId: string): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/projects/${encodeURIComponent(id)}/assets/${encodeURIComponent(assetId)}`);
  }

  unlinkAsset(id: string, assetId: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/projects/${encodeURIComponent(id)}/assets/${encodeURIComponent(assetId)}`);
  }

  update(id: string, payload: UpdateProjectPayload): Promise<ApiResult<string>> {
    return this.http.put<string>(`/api/projects/${encodeURIComponent(id)}`, { body: payload });
  }

  /**
   * Either `statusDefinitionId` (preferred) or `newStatus` (legacy, resolves to that
   * stage's default status) must be supplied — the backend rejects a body with
   * neither. The union makes that "exactly one of" contract the compiler's problem.
   */
  changeStatus(
    id: string,
    payload:
      | { statusDefinitionId: string; reason?: string | null }
      | { newStatus: ProjectStatus; reason?: string | null },
  ): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/projects/${encodeURIComponent(id)}/status`, { body: payload });
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/projects/${encodeURIComponent(id)}`);
  }

  notifyTimeplanCustomer(
    id: string,
    payload: NotifyTimeplanCustomerPayload,
  ): Promise<ApiResult<TimeplanCustomerNotificationDto>> {
    return this.http.post<TimeplanCustomerNotificationDto>(
      `/api/projects/${encodeURIComponent(id)}/timeplan/customer-notification`,
      { body: payload },
    );
  }

  /** Partners on a project (#4556). */
  listPartners(projectId: string): Promise<ApiResult<ProjectPartnerDto[]>> {
    return this.http.get<ProjectPartnerDto[]>(`/api/projects/${encodeURIComponent(projectId)}/partners`);
  }

  /** Adding a MainContractor sets the bill-to when it is empty and nothing is invoiced. */
  addPartner(projectId: string, payload: AddProjectPartnerPayload): Promise<ApiResult<ProjectPartnerDto>> {
    return this.http.post<ProjectPartnerDto>(`/api/projects/${encodeURIComponent(projectId)}/partners`, {
      body: payload,
    });
  }

  updatePartner(
    projectId: string,
    partnerId: string,
    payload: UpdateProjectPartnerPayload,
  ): Promise<ApiResult<ProjectPartnerDto>> {
    return this.http.put<ProjectPartnerDto>(
      `/api/projects/${encodeURIComponent(projectId)}/partners/${encodeURIComponent(partnerId)}`,
      { body: payload },
    );
  }

  removePartner(projectId: string, partnerId: string): Promise<ApiResult<RemoveProjectPartnerResult>> {
    return this.http.delete<RemoveProjectPartnerResult>(
      `/api/projects/${encodeURIComponent(projectId)}/partners/${encodeURIComponent(partnerId)}`,
    );
  }

  /**
   * "Send besked": mails the partner a tidsplan link and a "Prøv Mesterplan" link. Needs
   * projects.edit and partners.invite; answers 400 when the partner customer has no email.
   */
  notifyPartner(projectId: string, partnerId: string): Promise<ApiResult<ProjectPartnerDto>> {
    return this.http.post<ProjectPartnerDto>(
      `/api/projects/${encodeURIComponent(projectId)}/partners/${encodeURIComponent(partnerId)}/notify`,
    );
  }
}
